import * as yup from 'yup';
import { HelpCircle, Lock, Unlock } from 'react-feather';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-form-state-manager';

import { DateTimeForm } from '../../../Common/DateRangeForm';
import { Event } from './helpers';
import { Permission } from '../../../../__generated__/graphql';
import { encodeUrl, getServerErrors } from '../../../../common/helpers';
import ProtectedSection from '../../../Common/ProtectedSection';
import UI from '../../../../common/UI';
import useEditEventMutation from './useEditEventMutation';
import useLocale from '../../../../common/useLocale';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useValidation from '../../../../common/useValidation';

const editEventDashboardSettingsSchema = yup.object({
  registrations_locked_at: yup.string().nullable(),
});

export interface EditEventDashboardSettingsFormProps {
  event: Event;
}

const EditEventDashboardSettingsForm = ({ event }: EditEventDashboardSettingsFormProps) => {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const project = useProject();

  const getValues = (event: Event) => ({
    registrations_locked_at: !event.registrations_locked ? event.registrations_locked_at : null,
    registrations_locked: event.registrations_locked,
    reassignable_tickets: event.tickets.filter((ticket) => ticket.allow_reassignments).map((ticket) => ticket.id),
    exchangeable_tickets: event.tickets.filter((ticket) => ticket.allow_exchanges).map((ticket) => ticket.id),
  });

  const form = useForm({
    values: getValues(event),
  });

  useNavigationPrompt(form.changed());

  const now = formatDate(new Date(), { format: 'internal_date_time', timezone: 'UTC' });

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      dashboard: {
        registrations_locked_at: form.values.registrations_locked ? now : form.values.registrations_locked_at,
        reassignable_tickets: form.values.reassignable_tickets,
        exchangeable_tickets: form.values.exchangeable_tickets,
      },
    },
    onSuccess: (event) => {
      form.reset(getValues(event));
    },
  });

  const { valid, errors } = useValidation({
    schema: editEventDashboardSettingsSchema,
    values: form.values,
    externalErrors: getServerErrors(error, 'dashboard'),
  });

  const allTicketsReassignable = form.values.reassignable_tickets.length === event.tickets.length;

  const handleToggleAllReassignableTickets = () => {
    form.set((values) => ({
      ...values,
      reassignable_tickets: allTicketsReassignable ? [] : event.tickets.map((ticket) => ticket.id),
    }));
  };

  const exchangeableTickets = event.tickets.filter((ticket) => ticket.allowable_for_exchanges);
  const allTicketsExchangeable = form.values.exchangeable_tickets.length === exchangeableTickets.length;

  const handleToggleAllExchangeableTickets = () => {
    form.set((values) => ({
      ...values,
      exchangeable_tickets: allTicketsExchangeable ? [] : exchangeableTickets.map((ticket) => ticket.id),
    }));
  };

  const lockedSequences = event.ticket_number_sequences.filter((sequence) => sequence.locked);

  return (
    <UI.Form onSubmit={() => editEvent()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} sc={{ mb: 4 }} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('participant_dashboard')}</UI.Legend>
          <UI.InputDescription>
            {t('event_dashboard_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.InputGroup>
          <UI.FormGrid sc={{ gutter: 0.5 }}>
            <UI.Radio
              onChange={() => form.set((values) => ({ ...values, registrations_locked: false }))}
              checked={!form.values.registrations_locked}
              sc={{ block: true, box: true }}
            >
              <UI.Strong sc={{ block: true }}>
                <UI.Icon>
                  <Unlock />
                </UI.Icon>
                {' '}
                {t('allow_changes_via_dashboard')}
              </UI.Strong>
              {t('allow_changes_via_dashboard_description')}
            </UI.Radio>
            <UI.Radio
              onChange={() => form.set((values) => ({ ...values, registrations_locked: true }))}
              checked={form.values.registrations_locked}
              sc={{ block: true, box: true }}
            >
              <UI.Strong sc={{ block: true }}>
                <UI.Icon>
                  <Lock />
                </UI.Icon>
                {' '}
                {t('disallow_changes_via_dashboard')}
              </UI.Strong>
              {t('disallow_changes_via_dashboard_description')}
            </UI.Radio>
          </UI.FormGrid>
        </UI.InputGroup>

        {!form.values.registrations_locked && (
          <UI.FadeIn>
            <UI.FormGrid>
              <DateTimeForm
                form={form}
                errors={errors}
                keys={{ date: 'registrations_locked_at', time: 'time' }}
              />

              {event.tickets.length > 0 && (
                <UI.FormGrid sc={{ gutter: 0.25 }}>
                  <UI.FormGrid sc={{ columns: '2fr 1fr 1fr', gutter: 0.5 }}>
                    <UI.Div>
                      <UI.InputLabel>
                        {t('ticket')}
                      </UI.InputLabel>
                    </UI.Div>
                    <UI.Div sc={{ textAlign: 'center' }}>
                      <UI.TooltipContainer>
                        <UI.InputLabel>
                          {t('reassignable')}
                          {' '}
                          <UI.Icon sc={{ muted: true }}>
                            <HelpCircle />
                          </UI.Icon>
                        </UI.InputLabel>
                        <UI.Tooltip sc={{ alignH: 'center' }} style={{ width: 250, whiteSpace: 'normal' }}>
                          {t('tickets_reassignable_description')}
                        </UI.Tooltip>
                      </UI.TooltipContainer>
                    </UI.Div>
                    <UI.Div sc={{ textAlign: 'center' }}>
                      <UI.TooltipContainer>
                        <UI.InputLabel>
                          {t('exchangeable')}
                          {' '}
                          <UI.Icon sc={{ muted: true }}>
                            <HelpCircle />
                          </UI.Icon>
                        </UI.InputLabel>
                        <UI.Tooltip sc={{ alignH: 'center' }} style={{ width: 250, whiteSpace: 'normal' }}>
                          {t('tickets_exchangeable_description')}
                        </UI.Tooltip>
                      </UI.TooltipContainer>
                    </UI.Div>
                  </UI.FormGrid>
                  <UI.HR sc={{ my: 0 }} />
                  <UI.FormGrid sc={{ columns: '2fr 1fr 1fr', gutter: 0.5 }}>
                    <UI.Div sc={{ italic: true, muted: true }}>
                      {t('toggle_all')}
                    </UI.Div>
                    <UI.Div sc={{ textAlign: 'center' }}>
                      <UI.Checkbox
                        style={{ width: 13, margin: '0 auto' }}
                        checked={allTicketsReassignable}
                        onChange={handleToggleAllReassignableTickets}
                        aria-label={`${t('reassignable')} · ${t('toggle_all')}`}
                      />
                    </UI.Div>
                    <UI.Div sc={{ textAlign: 'center' }}>
                      <UI.Checkbox
                        style={{ width: 13, margin: '0 auto' }}
                        checked={allTicketsExchangeable}
                        onChange={handleToggleAllExchangeableTickets}
                        aria-label={`${t('exchangeable')} · ${t('toggle_all')}`}
                      />
                    </UI.Div>
                  </UI.FormGrid>
                  <UI.HR sc={{ my: 0 }} />
                  {event.tickets.map((ticket) => (
                    <UI.FormGrid sc={{ columns: '2fr 1fr 1fr', gutter: 0.5 }} key={ticket.id}>
                      <UI.Div>
                        {ticket.title}
                      </UI.Div>
                      <UI.Div sc={{ textAlign: 'center' }}>
                        <UI.Checkbox
                          {...form.checklist('reassignable_tickets', ticket.id)}
                          style={{ width: 13, margin: '0 auto' }}
                          aria-label={`${t('reassignable')} · ${ticket.title}`}
                        />
                      </UI.Div>
                      <UI.Div sc={{ textAlign: 'center' }}>
                        <UI.Checkbox
                          {...form.checklist('exchangeable_tickets', ticket.id)}
                          style={{ width: 13, margin: '0 auto' }}
                          disabled={!ticket.allowable_for_exchanges}
                          aria-label={`${t('exchangeable')} · ${ticket.title}`}
                        />
                      </UI.Div>
                    </UI.FormGrid>
                  ))}
                </UI.FormGrid>
              )}

              <UI.InputGroup>
                <UI.InputLabel>
                  {t('locked_ticket_number_sequences')}
                </UI.InputLabel>
                {lockedSequences.length > 0 && (
                  <>
                    <UI.InputDescription>
                      {t('locked_ticket_number_sequences_description')}
                    </UI.InputDescription>
                    <UI.UL>
                      {lockedSequences.map((sequence) => (
                        <UI.LI key={sequence.id}>
                          <ProtectedSection permissions={[Permission.participant_logistics]} fallback={sequence.title}>
                            <Link to={`/${project.id}/participants/bib-numbers/${sequence.id}?${encodeUrl({ event: event.id })}`}>
                              {sequence.title}
                            </Link>
                          </ProtectedSection>
                        </UI.LI>
                      ))}
                    </UI.UL>
                  </>
                )}

                {lockedSequences.length === 0 && (
                  <UI.InputDescription>
                    {t('no_locked_ticket_number_sequences_description')}
                    <ProtectedSection permissions={[Permission.participant_logistics]}>
                      {' '}
                      <Link to={`/${project.id}/participants/bib-numbers?${encodeUrl({ event: event.id })}`}>
                        {t('manage_start_groups')}
                      </Link>
                    </ProtectedSection>
                  </UI.InputDescription>
                )}
              </UI.InputGroup>
            </UI.FormGrid>
          </UI.FadeIn>
        )}

        <UI.InputGroup>
          <UI.InputLabel>
            {t('ticket_resale')}
          </UI.InputLabel>
          <UI.InputDescription>
            <Trans i18nKey="event_resale_settings_description">
              <Link to={`/${project.id}/events/${event.id}/resale`} />
            </Trans>
          </UI.InputDescription>
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_event')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditEventDashboardSettingsForm;
